import React from 'react';
import { Button, FormControlLabel, Switch } from '@mui/material';
import useStripeExpressLink from 'hooks/useStripeExpressLink';
import StripeExpressOpenError from 'components/StripeExpressOpenError';
import { TALENT_PAYMENT_COPY, TalentPaymentViewState } from 'utils/talentPaymentState';

type Props = {
  paymentView: TalentPaymentViewState;
  /** Screen-scoped automation prefix: "talent-invoice" or "talent-invoice-modal". */
  testIdPrefix: string;
  /** Mark as Paid for invoices outside Pay Now. */
  onTogglePaid?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const BADGE_COLORS: Record<TalentPaymentViewState['state'], { background: string; color: string }> = {
  paid: { background: 'rgba(19, 202, 130, 0.14)', color: '#0B4598' },
  action_required: { background: 'rgba(251, 118, 114, 0.16)', color: '#0B4598' },
  unpaid: { background: 'rgba(11, 69, 152, 0.08)', color: '#0B4598' },
};

/**
 * T2-7494: the invoice screen's and pop-up's Payment Status, in Holly's copy:
 * badge, title (none for Pay Now PAID), message, Mark as Paid outside Pay Now,
 * and "Click here to resolve" when action is required.
 */
export default function TalentPaymentStatusDetails({ paymentView, testIdPrefix, onTogglePaid }: Props) {
  const { opening, failure, open } = useStripeExpressLink();
  const badge = BADGE_COLORS[paymentView.state];

  return (
    <div
      data-testid={`${testIdPrefix}-payment-${paymentView.display}`}
      className="talent-payment-status"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, fontSize: 14 }}
    >
      <span
        data-testid={`${testIdPrefix}-payment-badge`}
        style={{
          background: badge.background,
          color: badge.color,
          borderRadius: 999,
          padding: '2px 10px',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {paymentView.label}
      </span>
      {paymentView.title && <div style={{ fontWeight: 600 }}>{paymentView.title}</div>}
      <div>{paymentView.message}</div>
      {paymentView.canUseLegacyPaidToggle && onTogglePaid && (
        <FormControlLabel
          control={(
            <Switch
              checked={paymentView.isPaid}
              onChange={onTogglePaid}
              color="primary"
              inputProps={{ 'aria-label': paymentView.isPaid ? 'Marked as paid' : 'Mark as paid' }}
              data-testid={`${testIdPrefix}-mark-paid-switch`}
            />
          )}
          label={paymentView.isPaid ? 'Marked as paid' : 'Mark as paid'}
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
        />
      )}
      {paymentView.action === 'resolve_stripe' && (
        <>
          <Button
            variant="text"
            onClick={() => { open(); }}
            disabled={opening}
            data-testid={`${testIdPrefix}-payment-resolve-button`}
            className="no-fastclick"
            sx={{ padding: 0, textDecoration: 'underline', textTransform: 'none', fontSize: '0.875rem', color: '#0B4598' }}
          >
            {TALENT_PAYMENT_COPY.resolveButton}
          </Button>
          <StripeExpressOpenError failure={failure} testIdPrefix={`${testIdPrefix}-payment`} />
        </>
      )}
    </div>
  );
}
